import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { getApiScopeFromValue } from '@features/invitations/utils/getScopeFromParam';
import { usePermissions } from '@features/entity/hooks/usePermissions';
import { useDashboard } from '../hooks/useDashboard';
import WorkQueueSection from '../components/WorkQueueSection';
import ReportingProgressPanel from '../components/ReportingProgressPanel';
import ReportingChecklistPanel from '../components/ReportingChecklistPanel';
import OutgoingInvitationsCard from '../components/OutgoingInvitationsCard';
import RecentActivityFeed from '../components/RecentActivityFeed';
import FinanceSnapshot from '../components/FinanceSnapshot';
import DashboardSkeleton from '../components/DashboardSkeleton';
import DeadlinesCard from '../components/DeadlinesCard';
import PersonalInviteNotice from '../components/PersonalInviteNotice';
import ReopenRequestsNotice from '../components/ReopenRequestsNotice';
import { QuickActionsBar } from '../components/QuickActionsBar';
import { buildQuickActions, dashboardPermissionActionCodes } from '../utils/dashboardLocalActions';
import { invitesPath } from '../utils/dashboardPaths';
import { isDeadlineAlertType, mostUrgentChecklistPeriodId } from '../utils/dashboardData';
import { DASHBOARD_ALERT_TYPES } from '../constants/dashboardAlerts';
import {
  DASHBOARD_REPORTING_CHECKLIST_ID,
  checklistPeriodAnchorId,
} from '../constants/dashboardLayout';
import { getScopedActivityEntryCode } from '@features/entity/types/actionCodes';
import './dashboard.css';

const EntityDashboard: React.FC = () => {
  const params = useParams<{
    churchId?: string;
    districtId?: string;
    nationId?: string;
  }>();

  const churchId = Number(params.churchId) || 0;
  const districtId = Number(params.districtId) || 0;
  const nationId = Number(params.nationId) || 0;

  const scope = churchId > 0 ? 'CHURCH' : districtId > 0 ? 'DISTRICT' : 'NATION';
  const scopeId = churchId || districtId || nationId;
  const scopeSegment = getApiScopeFromValue(scope);

  const { data, loading, error, refresh, accessDenied } = useDashboard(scope, scopeId);

  const permissionCodes = useMemo(
    () => dashboardPermissionActionCodes(scope),
    [scope]
  );
  const { permittedActions, loading: permissionsLoading } = usePermissions({
    scopeType: scope,
    scopeId,
    actionCodes: permissionCodes,
  });

  const quickActions = useMemo(
    () => (permissionsLoading ? [] : buildQuickActions(scope, scopeId, permittedActions)),
    [permissionsLoading, scope, scopeId, permittedActions]
  );
  const canSaveActivity = useMemo(() => {
    const saveCode = getScopedActivityEntryCode(scope, 'save');
    return Boolean(saveCode && permittedActions.includes(saveCode));
  }, [scope, permittedActions]);

  // Page-owned checklist expansion so Deadlines can open a period.
  const [checklistOverrideId, setChecklistOverrideId] = useState<number | null | undefined>(
    undefined
  );
  const checklistExpandedId =
    checklistOverrideId !== undefined
      ? checklistOverrideId
      : mostUrgentChecklistPeriodId(data?.reporting_checklist ?? []);

  const openChecklistPeriod = useCallback((reportTagId: number) => {
    setChecklistOverrideId(reportTagId);
    window.setTimeout(() => {
      const el =
        document.getElementById(checklistPeriodAnchorId(reportTagId)) ??
        document.getElementById(DASHBOARD_REPORTING_CHECKLIST_ID);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, []);

  if (accessDenied) {
    return null;
  }

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error || !data) {
    return (
      <div className="dashboard-page">
        <Alert variant="danger" className="d-flex justify-content-between align-items-center">
          <span>{error ?? 'Unable to load dashboard.'}</span>
          <Button variant="outline-danger" size="sm" onClick={refresh}>
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  const alerts = data.alerts ?? [];
  const deadlineAlerts = alerts.filter((alert) => isDeadlineAlertType(alert.type));
  const inviteAlert = alerts.find(
    (alert) => alert.type === DASHBOARD_ALERT_TYPES.PERSONAL_INVITE_PENDING
  );
  const reopenAlert = alerts.find(
    (alert) => alert.type === DASHBOARD_ALERT_TYPES.TAG_REOPEN_REQUESTED
  );
  const hasCards = Boolean(
    deadlineAlerts.length ||
    inviteAlert ||
    reopenAlert ||
    data.work_queue ||
    data.reporting_checklist?.length ||
    data.reporting_progress?.length ||
    data.invitations ||
    data.recent_activity ||
    data.finance_snapshots?.length
  );

  return (
    <div className="dashboard-page">
      <header className="dashboard-title-row">
        <h1 className="dashboard-title-row__title">Dashboard</h1>
        <QuickActionsBar actions={quickActions} className="dashboard-title-row__actions" />
      </header>

      {hasCards ? (
        <div className="dashboard-grid">
          <div className="dashboard-col dashboard-col--main">
            {data.reporting_checklist && data.reporting_checklist.length > 0 && (
              <div className="dashboard-slot dashboard-slot--checklist">
                <ReportingChecklistPanel
                  periods={data.reporting_checklist}
                  scope={scope}
                  scopeId={scopeId}
                  canSaveActivity={canSaveActivity}
                  expandedId={checklistExpandedId}
                  onToggle={setChecklistOverrideId}
                />
              </div>
            )}
            {data.reporting_progress && data.reporting_progress.length > 0 && (
              <div className="dashboard-slot dashboard-slot--progress">
                <ReportingProgressPanel
                  periods={data.reporting_progress}
                  scope={scope}
                  scopeId={scopeId}
                />
              </div>
            )}
            {data.finance_snapshots && data.finance_snapshots.length > 0 && (
              <div className="dashboard-slot dashboard-slot--finance">
                <FinanceSnapshot
                  periods={data.finance_snapshots}
                  scope={scope}
                  scopeId={scopeId}
                />
              </div>
            )}
          </div>
          <div className="dashboard-col dashboard-col--side">
            {deadlineAlerts.length > 0 && (
              <div className="dashboard-slot dashboard-slot--deadlines">
                <DeadlinesCard alerts={deadlineAlerts} onOpenPeriod={openChecklistPeriod} />
              </div>
            )}
            {inviteAlert && (
              <div className="dashboard-slot dashboard-slot--notice">
                <PersonalInviteNotice alert={inviteAlert} scope={scope} scopeId={scopeId} />
              </div>
            )}
            {reopenAlert && (
              <div className="dashboard-slot dashboard-slot--notice">
                <ReopenRequestsNotice alert={reopenAlert} scope={scope} scopeId={scopeId} />
              </div>
            )}
            {data.work_queue && (
              <div className="dashboard-slot dashboard-slot--work">
                <WorkQueueSection
                  workQueue={data.work_queue}
                  scopeSegment={scopeSegment}
                  scopeId={scopeId}
                />
              </div>
            )}
            {data.invitations && (
              <div className="dashboard-slot dashboard-slot--invitations">
                <OutgoingInvitationsCard
                  invitations={data.invitations}
                  invitesPath={invitesPath(scope, scopeId)}
                />
              </div>
            )}
            {data.recent_activity && (
              <div className="dashboard-slot dashboard-slot--activity">
                <RecentActivityFeed items={data.recent_activity} />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="dashboard-empty-state" role="status">
          <div className="dashboard-empty-state__title">You're all caught up</div>
          <p className="dashboard-empty-state__sub mb-0">Nothing needs your attention right now.</p>
        </div>
      )}
    </div>
  );
};

export default EntityDashboard;
